import React from 'react'
import { CallableType, ClearingHouseType, ColumnMapping, HaircutType, RepoType, SponsorType, SpreadsheetCsd, SpreadsheetMapping } from 'api'
import {
  ColumnMappingDatePanel,
  ColumnMappingDecimalPanel,
  ColumnMappingDirectionPanel,
  ColumnMappingEnumPanel,
  ColumnMappingStringPanel,
  ColumnsContainer,
  ColumnsHeader,
  ColumnsHeaderPanel
} from '../upload/mappingDialog'
import { Locale, Translation } from 'locale'
import { OrderSpreadsheetParseResult } from './parseOrderSpreadsheet'
import { NOTE_INDEXES } from '../datagrid'
import {
  CallableTypeHelper,
  ClearingHouseTypeHelper,
  CollectionTypeHelper,
  HaircutTypeHelper,
  RepoTypeHelper,
  SponsorTypeHelper,
  SpreadsheetCsdHelper,
  SpreadsheetSettlementVenueHelper
} from 'model'
import { CollectionTypeMappingEnum, SpreadsheetSettlementVenue } from './utils'

export interface OrderMappingDialogColumnsProps {
  mapping: SpreadsheetMapping
  parseResult: OrderSpreadsheetParseResult
  onUpdate: (mapping: SpreadsheetMapping) => unknown
}

export const OrderMappingDialogColumns: React.FunctionComponent<OrderMappingDialogColumnsProps> = props => {
  const { mapping, parseResult, onUpdate } = props

  const handleColumnUpdate = (columnMapping: ColumnMapping, fieldName: keyof SpreadsheetMapping) =>
    onUpdate({ ...mapping, [fieldName]: columnMapping })

  const handleNotesColumnUpdate = (noteIndex: string) => (columnMapping: ColumnMapping, _fieldName: keyof SpreadsheetMapping) => {
    onUpdate({
      ...mapping,
      notes: {
        ...mapping.notes,
        [noteIndex]: columnMapping
      }
    })
  }

  return (
    <ColumnsContainer>
      <ColumnsHeaderPanel>
        <ColumnsHeader>{Locale.translate(Translation.TC_ORDER_FIELD)}</ColumnsHeader>
        <ColumnsHeader>{Locale.translate(Translation.TC_SPREADSHEET_COLUMN)}</ColumnsHeader>
      </ColumnsHeaderPanel>

      <ColumnMappingStringPanel
        fieldTitle={Locale.translate(Translation.TC_MY_ENTITY)}
        fieldName="fund"
        columnMapping={mapping.fund}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingStringPanel
        fieldTitle={Locale.translate(Translation.TC_ISIN_BUI)}
        fieldName="isin"
        columnMapping={mapping.isin}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingStringPanel
        fieldTitle={Locale.translate(Translation.TC_SECONDARY_ISIN_BUI)}
        fieldName="secondaryIsin"
        columnMapping={mapping.secondaryIsin}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingDirectionPanel
        fieldTitle={Locale.translate(Translation.TC_DIRECTION)}
        fieldName="direction"
        columnMapping={mapping.direction}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingDecimalPanel
        fieldTitle={Locale.translate(Translation.TC_NOTIONAL, { amountFormat: null })}
        fieldName="notional"
        columnMapping={mapping.notional}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingStringPanel
        fieldTitle={Locale.translate(Translation.TC_START_CONSIDERATION)}
        fieldName="startConsideration"
        columnMapping={mapping.startConsideration}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingStringPanel
        fieldTitle={Locale.translate(Translation.TC_REFERENCE_RATE)}
        fieldName="referenceRate"
        columnMapping={mapping.referenceRate}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingStringPanel
        fieldTitle={Locale.translate(Translation.TC_RATE)}
        fieldName="rate"
        columnMapping={mapping.rate}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingStringPanel
        fieldTitle={Locale.translate(Translation.TC_TERM)}
        fieldName="term"
        columnMapping={mapping.term}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingDatePanel
        fieldTitle={Locale.translate(Translation.TC_START_DATE)}
        fieldName="startDate"
        columnMapping={mapping.startDate}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingDatePanel
        fieldTitle={Locale.translate(Translation.TC_END_DATE)}
        fieldName="endDate"
        columnMapping={mapping.endDate}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingStringPanel
        fieldTitle={Locale.translate(Translation.TC_BOOK)}
        fieldName="book"
        columnMapping={mapping.book}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingStringPanel
        fieldTitle={Locale.translate(Translation.TC_STRATEGY)}
        fieldName="strategy"
        columnMapping={mapping.strategy}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingStringPanel
        fieldTitle={Locale.translate(Translation.TC_PRIME_BROKER)}
        fieldName="primeBroker"
        columnMapping={mapping.primeBroker}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingStringPanel
        fieldTitle={Locale.translate(Translation.TC_BUNDLE)}
        fieldName="bundle"
        columnMapping={mapping.bundle}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingEnumPanel
        fieldTitle={Locale.translate(Translation.TC_REPO_TYPE)}
        fieldName="repoType"
        columnMapping={mapping.repoType}
        parseResult={parseResult}
        Enum={RepoType}
        enumHelper={RepoTypeHelper}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingStringPanel
        fieldTitle={Locale.translate(Translation.TC_COLLATERAL_PRICE)}
        fieldName="bondPrice"
        columnMapping={mapping.bondPrice}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingStringPanel
        fieldTitle={Locale.translate(Translation.TC_POSITION_ID)}
        fieldName="externalId"
        columnMapping={mapping.externalId}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingStringPanel
        fieldTitle={Locale.translate(Translation.TC_COLLECTION_NAME)}
        fieldName="collectionName"
        columnMapping={mapping.collectionName}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingEnumPanel
        fieldTitle={Locale.translate(Translation.TC_COLLECTION_TYPE)}
        fieldName="collectionType"
        columnMapping={mapping.collectionType}
        parseResult={parseResult}
        Enum={CollectionTypeMappingEnum}
        enumHelper={CollectionTypeHelper}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingStringPanel
        fieldTitle={Locale.translate(Translation.TC_COUNTERPARTY)}
        fieldName="counterpartyName"
        columnMapping={mapping.counterpartyName}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingStringPanel
        fieldTitle={Locale.translate(Translation.TC_ROLL)}
        fieldName="roll"
        columnMapping={mapping.roll}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingStringPanel
        fieldTitle={Locale.translate(Translation.TC_UNWIND)}
        fieldName="unwind"
        columnMapping={mapping.unwind}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingStringPanel
        fieldTitle={Locale.translate(Translation.TC_SUB)}
        fieldName="sub"
        columnMapping={mapping.sub}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingStringPanel
        fieldTitle={Locale.translate(Translation.TC_AXE)}
        fieldName="axe"
        columnMapping={mapping.axe}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      {NOTE_INDEXES.map(index => (
        <ColumnMappingStringPanel
          key={index}
          fieldTitle={Locale.translate(Translation.TC_NOTE, { noteIndex: parseInt(index, 10) + 1 })}
          fieldName="notes"
          columnMapping={mapping.notes[index]}
          parseResult={parseResult}
          onUpdate={handleNotesColumnUpdate(index)}
        />
      ))}
      <ColumnMappingStringPanel
        fieldTitle={Locale.translate(Translation.TC_PRE_AGREED)}
        fieldName="preAgreed"
        columnMapping={mapping.preAgreed}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingEnumPanel
        fieldTitle={Locale.translate(Translation.TC_CALLABLE)}
        fieldName="callableType"
        columnMapping={mapping.callableType}
        parseResult={parseResult}
        Enum={CallableType}
        enumHelper={CallableTypeHelper}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingStringPanel
        fieldTitle={Locale.translate(Translation.TC_CALLABLE_PERIOD)}
        fieldName="callablePeriodDays"
        columnMapping={mapping.callablePeriodDays}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingStringPanel
        fieldTitle={Locale.translate(Translation.TC_EVERGREEN_NOTICE_PERIOD)}
        fieldName="evergreenNoticePeriod"
        columnMapping={mapping.evergreenNoticePeriod}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingEnumPanel
        fieldTitle={Locale.translate(Translation.TC_SETTLEMENT_TYPE)}
        fieldName="settlementVenue"
        columnMapping={mapping.settlementVenue}
        parseResult={parseResult}
        Enum={SpreadsheetSettlementVenue}
        enumHelper={SpreadsheetSettlementVenueHelper}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingEnumPanel
        fieldTitle={Locale.translate(Translation.TC_CCP)}
        fieldName="clearingHouse"
        columnMapping={mapping.clearingHouse}
        parseResult={parseResult}
        Enum={ClearingHouseType}
        enumHelper={ClearingHouseTypeHelper}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingEnumPanel
        fieldTitle={Locale.translate(Translation.TC_SPONSOR)}
        fieldName="sponsor"
        columnMapping={mapping.sponsor}
        parseResult={parseResult}
        Enum={SponsorType}
        enumHelper={SponsorTypeHelper}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingEnumPanel
        fieldTitle={Locale.translate(Translation.TC_CSD)}
        fieldName="csd"
        columnMapping={mapping.csd}
        parseResult={parseResult}
        Enum={SpreadsheetCsd}
        enumHelper={SpreadsheetCsdHelper}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingStringPanel
        fieldTitle={Locale.translate(Translation.TC_ROE)}
        fieldName="rightOfExit"
        columnMapping={mapping.rightOfExit}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingStringPanel
        fieldTitle={Locale.translate(Translation.TC_ROS)}
        fieldName="rightOfSubstitution"
        columnMapping={mapping.rightOfSubstitution}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingEnumPanel
        fieldTitle={Locale.translate(Translation.TC_HAIRCUT_TYPE)}
        fieldName="haircutType"
        columnMapping={mapping.haircutType}
        parseResult={parseResult}
        Enum={HaircutType}
        enumHelper={HaircutTypeHelper}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingStringPanel
        fieldTitle={Locale.translate(Translation.TC_HAIRCUT)}
        fieldName="haircut"
        columnMapping={mapping.haircut}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
    </ColumnsContainer>
  )
}
